import {useMutation} from '@apollo/client'
import React, {useCallback, useEffect, useState} from 'react'
import {Alert, Button, Modal, Spinner} from 'react-bootstrap'
import {HARD_DELETE_COMMISSION_REPORT} from '../../../gql/mutations/commissionReportMutations'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {IResHardDeleteCommissionReport} from '../../../types/commissionReportApi'

interface Props {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit: {id: string; name: string}
}

type Step = 'confirm1' | 'confirm2'

export const ModalHardDeleteCommissionReport: React.FC<Props> = ({
  infoEdit,
  isOpen,
  onClose,
  refetch,
}) => {
  const [step, setStep] = useState<Step>('confirm1')
  const [gqlHardDelete, {loading, error}] = useMutation<IResHardDeleteCommissionReport>(
    HARD_DELETE_COMMISSION_REPORT
  )

  const handleClose = useCallback(() => {
    setStep('confirm1')
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) setStep('confirm1')
  }, [isOpen])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const handleExecute = async () => {
    try {
      const {data} = await gqlHardDelete({variables: {id: parseId(infoEdit.id)}})
      if (data?.commissionReportHardDeleteMutation?.success) {
        toast.success('Commission report permanently deleted.')
        refetch()
        handleClose()
      } else {
        const msg =
          data?.commissionReportHardDeleteMutation?.errors?.[0]?.message ||
          'Unable to delete report.'
        toast.error(`Error: ${msg}`)
      }
    } catch (e: any) {
      toast.error(`Error: ${e.message}`)
    }
  }

  return (
    <Modal show={isOpen} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Permanently Delete Commission Report</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant={step === 'confirm1' ? 'warning' : 'danger'} className='mb-3'>
          <Alert.Heading>
            <i
              className={`bi ${
                step === 'confirm1'
                  ? 'bi-exclamation-triangle-fill'
                  : 'bi-exclamation-octagon-fill'
              } me-2`}
            />
            {step === 'confirm1' ? 'Are you sure?' : 'Final confirmation'}
          </Alert.Heading>
          <p className='mb-0'>
            {step === 'confirm1' ? (
              <>
                You are about to <strong>permanently delete</strong> the report{' '}
                <strong>"{infoEdit.name}"</strong>. This action{' '}
                <strong>cannot be undone</strong>.
              </>
            ) : (
              <>
                Are you <strong>absolutely sure</strong>? This will{' '}
                <strong>permanently and irreversibly delete</strong> the report{' '}
                <strong>"{infoEdit.name}"</strong> and all its associated data.
              </>
            )}
          </p>
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant='light'
          onClick={() => (step === 'confirm1' ? handleClose() : setStep('confirm1'))}
        >
          {step === 'confirm1' ? 'Cancel' : 'Go back'}
        </Button>
        {step === 'confirm1' ? (
          <Button variant='warning' onClick={() => setStep('confirm2')}>
            Yes, continue
          </Button>
        ) : (
          <Button variant='danger' onClick={handleExecute} disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            <i className='bi bi-trash-fill me-1' /> Yes, delete permanently
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
